import {
  SamlSskService,
  SskSigningFailureReason as SskFailureBucket,
  SskSigningFailureReport,
} from '@/api/generated';

// Buckets and copy follow the SPRK-6789 product spec; add a bucket here and
// every SSK page picks it up through SSK_FAILURE_COPY.
// The bucket enum is the server contract; web-login reuses it as-is.
export { SskFailureBucket };
export const SSK_SSO_TYPE = SskSigningFailureReport.sso_type;

export enum SskFailureAction {
  CONTACT_SUPPORT = 'contact_support',
  RETRY = 'retry',
}

export interface SskFailureCopy {
  headline: string;
  body: string;
  primaryAction: SskFailureAction;
  secondary?: string;
}

export const CONTACT_SUPPORT_LABEL = 'Contact IT support';
export const RETRY_LABEL = 'Try again';

export const SSK_FAILURE_COPY: Record<SskFailureBucket, SskFailureCopy> = {
  [SskFailureBucket.NO_SHARE]: {
    headline: "We can't finish signing you in on this device",
    body:
      'This app needs the NewCore browser extension to complete sign-in, and we ' +
      "couldn't find it here. If your IT team manages your browser, they can set " +
      'this up for you.',
    primaryAction: SskFailureAction.CONTACT_SUPPORT,
    secondary: 'Already have the extension installed? Try refreshing the page.',
  },
  [SskFailureBucket.TIMEOUT]: {
    headline: 'That took too long',
    body: "Sign-in didn't finish in time. This usually clears up on a retry.",
    primaryAction: SskFailureAction.RETRY,
    secondary: 'Still stuck after a couple of tries? Contact IT support.',
  },
  [SskFailureBucket.UNSUPPORTED_BROWSER]: {
    headline: "This browser isn't supported for secure sign-in",
    body:
      'This app requires a supported browser to complete sign-in securely. ' +
      'Please try again in Chrome or Edge.',
    primaryAction: SskFailureAction.CONTACT_SUPPORT,
    secondary:
      'Using Safari or Arc? Switch to a supported browser and try again.',
  },
  [SskFailureBucket.INVALID_TOKEN]: {
    headline: "We couldn't complete sign-in",
    body: 'Something went wrong finishing sign-in. Please try again.',
    primaryAction: SskFailureAction.RETRY,
    secondary: 'Keeps happening? Contact IT support.',
  },
  [SskFailureBucket.UNKNOWN]: {
    headline: "We couldn't complete sign-in",
    body:
      'Something went wrong while signing you in to this app. Please try ' +
      'again, or reach out to your IT team if it continues.',
    primaryAction: SskFailureAction.RETRY,
    secondary: 'Still having trouble? Contact IT support.',
  },
};

export interface SskSigningErrorContext {
  detail?: string;
  extensionVersion?: string;
}

export class SskSigningError extends Error {
  readonly detail?: string;
  readonly extensionVersion?: string;

  constructor(
    readonly bucket: SskFailureBucket,
    message: string,
    context: SskSigningErrorContext = {}
  ) {
    super(message);
    this.name = 'SskSigningError';
    this.detail = context.detail;
    this.extensionVersion = context.extensionVersion;
  }
}

// auth's split-OIDC complete-signing 401 error_code values.
const SERVER_ERROR_CODE_BUCKETS: Partial<Record<string, SskFailureBucket>> = {
  SSK_CODE_EXPIRED: SskFailureBucket.TIMEOUT,
  SSK_CODE_INVALID: SskFailureBucket.INVALID_TOKEN,
  SSK_SESSION_MISMATCH: SskFailureBucket.INVALID_TOKEN,
  SSK_SIGNATURE_ALREADY_SUBMITTED: SskFailureBucket.INVALID_TOKEN,
};

// auth's complete-signing 401 carries {"detail": {"message", "error_code"}}.
function serverErrorCode(error: unknown): string | undefined {
  const detail = (error as { body?: { detail?: unknown } } | null)?.body
    ?.detail;

  return typeof detail === 'object' && detail !== null
    ? (detail as { error_code?: string }).error_code
    : undefined;
}

export function toSskFailureBucket(error: unknown): SskFailureBucket {
  if (error instanceof SskSigningError) {
    return error.bucket;
  }

  return (
    SERVER_ERROR_CODE_BUCKETS[serverErrorCode(error) ?? ''] ??
    SskFailureBucket.UNKNOWN
  );
}

export interface SskSupportMailto {
  supportEmail: string;
  appName: string;
  username: string;
  referenceId: string;
}

function browserAndOs(): string {
  const data = (
    navigator as Navigator & {
      userAgentData?: {
        brands: { brand: string; version: string }[];
        platform: string;
      };
    }
  ).userAgentData;
  const brand = data?.brands.find(b => !/Not.A.Brand|Chromium/i.test(b.brand));

  return brand && data
    ? `${brand.brand} ${brand.version} / ${data.platform}`
    : navigator.userAgent;
}

// The user emails IT themselves (no automated admin mail yet). Product-defined
// structure: generic subject, neutral context block, room for free text.
export function supportMailto({
  supportEmail,
  appName,
  username,
  referenceId,
}: SskSupportMailto): string {
  if (!supportEmail) {
    return '';
  }

  const app = appName || 'the application';
  const body = [
    `User: ${username}`,
    `App: ${app}`,
    `Browser / OS: ${browserAndOs()}`,
    `Time: ${new Date().toISOString()}`,
    `Reference ID: ${referenceId}`,
    'Additional details: ',
  ].join('\n');

  return `mailto:${supportEmail}?subject=${encodeURIComponent(
    `Help signing in to ${app}`
  )}&body=${encodeURIComponent(body)}`;
}

// Best-effort: the failure screen never waits on, or fails because of, the report.
export interface SskFailureReport {
  ssoType: SskSigningFailureReport.sso_type;
  appId: string;
  bucket: SskFailureBucket;
  error: unknown;
  referenceId: string;
}

export async function reportSskFailure({
  ssoType,
  appId,
  bucket,
  error,
  referenceId,
}: SskFailureReport): Promise<void> {
  const context = error instanceof SskSigningError ? error : undefined;

  try {
    await SamlSskService.reportSskSigningFailure({
      sso_type: ssoType,
      reason: bucket,
      detail: context?.detail ?? serverErrorCode(error) ?? null,
      app_id: appId || null,
      extension_version: context?.extensionVersion ?? null,
      reference_id: referenceId,
    });
  } catch (reportError) {
    console.warn('SSK failure report not delivered:', reportError);
  }
}
